import { useSyncExternalStore } from 'react';
import type { Role } from '@/domain/role';
import { roleFromCode } from '@/domain/role';
import { STORAGE_KEYS, storage } from '@/shared/lib/storage';

/**
 * Single source of truth for "who is the logged-in user". Reads localStorage that
 * the login flow populates. Subscribes to `storage` events so other tabs and the
 * logout flow trigger a re-render.
 *
 * NEVER read localStorage['role'] elsewhere — ESLint blocks it. Use this hook.
 */
type CurrentUser = {
  role: Role;
  token: string | null;
  fullName: string | null;
  isAuthenticated: boolean;
};

const subscribe = (callback: () => void): (() => void) => {
  window.addEventListener('storage', callback);
  window.addEventListener('app:auth-changed', callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener('app:auth-changed', callback);
  };
};

// Cached snapshot so `getSnapshot` returns a stable reference between subscribe
// events. `useSyncExternalStore` calls `Object.is` on the result; without this
// cache every consumer would re-render on every render of any consumer.
let cachedSnapshot: CurrentUser | null = null;

const fieldsEqual = (a: CurrentUser, b: CurrentUser): boolean =>
  a.role === b.role &&
  a.token === b.token &&
  a.fullName === b.fullName &&
  a.isAuthenticated === b.isAuthenticated;

const getSnapshot = (): CurrentUser => {
  const token = storage.getString(STORAGE_KEYS.idToken);
  const roleCode = storage.getString(STORAGE_KEYS.role);
  const fullName = storage.getString(STORAGE_KEYS.fullName);
  const next: CurrentUser = {
    role: roleFromCode(roleCode),
    token,
    fullName,
    isAuthenticated: token !== null && token.length > 0,
  };
  if (cachedSnapshot && fieldsEqual(cachedSnapshot, next)) {
    return cachedSnapshot;
  }
  cachedSnapshot = next;
  return next;
};

const serverSnapshot: CurrentUser = {
  role: 'unknown',
  token: null,
  fullName: null,
  isAuthenticated: false,
};

const getServerSnapshot = (): CurrentUser => serverSnapshot;

export const useCurrentUser = (): CurrentUser =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

/** Fire from login/logout flows so the hook re-renders the rest of the app. */
export const emitAuthChanged = (): void => {
  window.dispatchEvent(new Event('app:auth-changed'));
};
